import type { Map as MapLibreMap } from 'maplibre-gl';
import { TERRAIN_SOURCE } from './terrainStyle';

const EXAGGERATION = 1.2;

export interface TerrainController {
  /** 3D: terrain on, tilted, tilting allowed. 2D: terrain off, flat, tilting locked. */
  setEnabled: (enabled: boolean) => void;
  dispose: () => void;
}

/**
 * Switches between a flat 2D map and tilted 3D terrain around MapLibre 4 quirks. Terrain stays off in 2D:
 * drawing it from the start makes the map jump once the elevation tiles under the centre load.
 *
 * Terrain is only switched while the camera is still. MapLibre's flyTo/easeTo set up their elevation
 * tracking only if terrain was on when they started; turning it on mid-flight (e.g. while flying to a
 * search result) makes the next animation frame throw, freezing the map mid-flight and leaving it blank
 * until the user drags it. Tilting right away would also cancel that flight.
 *
 * Tilting waits for the elevation tiles: easeTo fixes the height of the point it keeps centred when it
 * starts, and before the tiles load that is sea level. In the mountains the camera then aims kilometres
 * below the ground and the spot you were looking at slides far up the screen (the view "shifts south").
 */
export function createTerrainController(map: MapLibreMap): TerrainController {
  const maxPitch = map.getMaxPitch();
  let wanted = false;

  /** 2D locks the pitch at 0, so no gesture (two fingers, right-drag, keyboard) can tilt the map. */
  function allowTilt(allow: boolean) {
    map.setMaxPitch(allow ? maxPitch : 0);
    if (allow) map.touchPitch.enable();
    else map.touchPitch.disable();
  }

  function apply() {
    if (wanted) {
      allowTilt(true);
      if (!map.getTerrain()) map.setTerrain({ source: TERRAIN_SOURCE, exaggeration: EXAGGERATION });
      map.once('idle', () => {
        if (wanted && map.getTerrain() && !map.isMoving()) map.easeTo({ pitch: maxPitch });
      });
      return;
    }
    map.setTerrain(null);
    if (map.getPitch() === 0) return allowTilt(false);
    map.easeTo({ pitch: 0 });
    map.once('moveend', () => !wanted && allowTilt(false));
  }

  // MapLibre 4 freezes the centre elevation for every easeTo/flyTo on terrain but only unfreezes it when
  // the animation was started with freezeElevation, so afterwards the camera keeps aiming at whatever
  // height it had (often sea level at a fly-to destination whose tiles had not loaded yet). Unfreeze it
  // once the camera stops; the render loop then tracks the ground under the centre again.
  const unfreezeElevation = () => {
    if (map.getTerrain()) (map as unknown as { _elevationFreeze: boolean })._elevationFreeze = false;
  };
  map.on('moveend', unfreezeElevation);

  return {
    setEnabled(enabled) {
      wanted = enabled;
      map.off('moveend', apply);
      if (map.isMoving()) map.once('moveend', apply);
      else apply();
    },
    dispose() {
      map.off('moveend', unfreezeElevation);
      map.off('moveend', apply);
      // Leave the map as found, so a new controller (StrictMode re-runs effects) reads the real max pitch.
      allowTilt(true);
    },
  };
}
